import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { apiBaseUrl } from '../config/api.config';
import { ApiError } from '../models/errors/api-error.model';
import { EcommerceSessionService } from '../services/ecommerce/ecommerce-session.service';

const ECOMMERCE_URL_PREFIX = `${apiBaseUrl}/api/v1/ecommerce`;
const ECOMMERCE_LOGIN_URL = `${ECOMMERCE_URL_PREFIX}/auth/login`;

/**
 * Allega il token di sessione ecommerce legacy (opaco) alle chiamate
 * `/api/v1/ecommerce/*` quando esiste una sessione valida e nessun altro
 * interceptor ha già messo un Authorization: con l'utente autenticato su
 * Keycloak l'header arriva già da `includeBearerTokenInterceptor` (vedi
 * `apiBearerCondition`) e qui non viene toccato. Su 401 la sessione locale
 * viene eliminata (senza toccare l'eventuale carrello guest), come da
 * contratto API.
 */
export const ecommerceBearerInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(ECOMMERCE_URL_PREFIX)) {
    return next(req);
  }

  const sessionService = inject(EcommerceSessionService);

  const token = sessionService.accessToken;
  const request =
    token && !req.headers.has('Authorization')
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(request).pipe(
    catchError((error: unknown) => {
      // Il login non deve mai invalidare una sessione esistente: una password
      // sbagliata è un errore dell'utente, non una sessione scaduta.
      const isLoginCall = req.method === 'POST' && req.url === ECOMMERCE_LOGIN_URL;

      // Sessione non più valida: arriva come 401 HTTP oppure, col nuovo
      // contratto, come errore applicativo con original_status 401.
      const isUnauthorized =
        (error instanceof HttpErrorResponse && error.status === 401) ||
        (error instanceof ApiError && error.originalStatus === 401);

      if (isUnauthorized && !isLoginCall) {
        sessionService.clearSession();
      }
      return throwError(() => error);
    })
  );
};
